import {
  Breadcrumb,
  Callout,
  DatePicker,
  DayOfWeek,
  DetailsList,
  DetailsListLayoutMode,
  DetailsRow,
  DirectionalHint,
  Dropdown,
  DropdownMenuItemType,
  Fabric,
  IColumn,
  IDetailsListProps,
  IDetailsRowStyles,
  IDropdownOption,
  ISelectableOption,
  PrimaryButton,
  SelectionMode,
  Stack,
  Text,
  TextField,
  Toggle,
  TooltipHost,
} from "@fluentui/react";
import * as React from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  getADataCenter,
  getFloorsWhichHasMorSensors,
  searchQuadrantsOfDC,
} from "../../../services/datacenter/DatacenterService";
import {
  getMorTempAndRhReadings,
  getMorTempAndRhReadingsRackWise,
} from "../../../services/infrastructure-automation/InfrastructureAutmationService";
import { Loading } from "../../../services/Loading";
import { ToastContext } from "../../common/ToastProvider";
import moment from "moment";
import {
  IDataCenter,
  IMorTempRhReadings,
  IMorTempRhReadingsRackWise,
  ISLAMatrix,
  ITempRhReadings,
} from "../../../vm";
import { childrenGap, childrenGap8 } from "../../common/Constants";
import NoDataTile from "../../common/NoDataTile";
import { getSLAMatrixConfig } from "../../../services/sla-matrix/SlaMatrixService";
import {
  mdiArrowDown,
  mdiArrowUp,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiDotsVertical,
  mdiEyeOutline,
} from "@mdi/js";
import { THEME } from "../../../utils/Styles";
import Icon from "@mdi/react";
import ManageTempAndRhDialog from "./ManageTempAndRhDialog";
import { toFixedValue } from "../../../services/UtilService";
import { updateTempAndHumidity } from "../../../services/customers/CustomerService";

export interface TempAndRhMainProps {
  datacenterId: string;
}

var quadrantDict: any = {};
var slaMatrix: any = {};
var slaMatrixDictByCustomer: any = {};

const TempAndRhMain: React.FC<TempAndRhMainProps> = ({ datacenterId }) => {
  // hooks
  const params: any = useParams();

  const { showToast } = React.useContext(ToastContext);
  const history = useHistory();
  // state
  const [isLoading, setLoading] = React.useState(false);
  const [isRackWiseView, setIsRackWiseView] = React.useState(false);
  const [state, setState] = React.useState({
    tempRhObj: {
      date: new Date(),
      temp: 0,
      rh: 0,
      sensorId: "",
      quadrantId: "",
    },
    manageActionsCallout: {
      isOpen: false,
      index: undefined,
      element: undefined,
      data: undefined,
    },
    manageTempRhDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    isOnEdit: false,
  } as {
    tempRhObj: ITempRhReadings;
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      index?: number;
      data?: IMorTempRhReadings;
    };
    manageTempRhDialog: {
      isOpen: boolean;
      index?: number;
      data?: IMorTempRhReadings;
    };
    isOnEdit: boolean;
  });
  const [floors, setFloors] = React.useState([] as IDropdownOption[]);
  const [datacenter, setDatacenter] = React.useState({} as IDataCenter);
  const [search, setSearch] = React.useState({
    datacenterId: datacenterId,
    floorId: "",
    startDate: moment().startOf("day").toDate(),
    endDate: moment().startOf("day").toDate(),
  } as {
    datacenterId: string;
    floorId: string;
    startDate: Date | string;
    endDate: Date | string;
  });

  const [readings, setReadings] = React.useState([] as IMorTempRhReadings[]);
  const [rackWiseReadings, setRackWiseReadings] = React.useState(
    [] as IMorTempRhReadingsRackWise[]
  );

  // local hook
  React.useLayoutEffect(() => {
    const asyncFunc = async () => {
      const result = await searchMetaDataForSearch(datacenterId);
    };
    asyncFunc();
  }, []);

  const searchMetaDataForSearch = async (datacenterId: string) => {
    // setState(true);

    let result = await getADataCenter(datacenterId);
    if (result?.success) {
      setDatacenter(result.data);
      const fResult = await getFloorsWhichHasMorSensors({
        datacenterId: datacenterId,
        type: "temp-rh",
      });

      // sla matrix
      let slaResult = await getSLAMatrixConfig(params.customerId);
      if (slaResult?.success) {
        slaMatrix = slaResult.data;
      }

      // get Quadrant details
      const qResult = await searchQuadrantsOfDC(datacenterId);
      if (qResult && qResult.success) {
        quadrantDict = qResult.data.reduce((acc: any, d) => {
          acc[d._id as string] = d.quadrant;
          return acc;
        }, {});
      }

      // get floor details
      if (fResult && fResult.success) {
        const floorOptions: IDropdownOption[] = [];
        let tempB: any;
        fResult.data.forEach((d, i) => {
          if (tempB == null || tempB !== d.buildingId) {
            if (tempB != null) {
              floorOptions.push({
                key: "divider_1",
                text: "-",
                itemType: DropdownMenuItemType.Divider,
              });
            }
            floorOptions.push({
              key: d.buildingId,
              text: d.buildingName,
              itemType: DropdownMenuItemType.Header,
            });
            tempB = d.buildingId;
          }
          floorOptions.push({
            key: d.floorId,
            text: d.floorName,
            data: { buildingName: d.buildingName },
          });
        });
        setFloors(floorOptions);
        if (floorOptions.length > 1) {
          setSearch({ ...search, floorId: floorOptions[1].key as string });
          await searchMorReadings(floorOptions[1].key as string);
        }
      }
    } else {
      showToast(result?.message || "Error while getting datacenter", "error");
      setLoading(false);
    }
  };

  const searchMorReadings = async (floorId?: string) => {
    setLoading(true);
    const searchObj = { ...search };
    if (floorId) {
      searchObj["floorId"] = floorId;
    }
    searchObj.startDate = moment(searchObj.startDate).format("YYYY-MM-DD");
    searchObj.endDate = moment(searchObj.endDate).format("YYYY-MM-DD");

    const result = await getMorTempAndRhReadings(searchObj);
    if (result && result.success) {
      // console.log("quadrantDict", quadrantDict);
      const data = result.data.map((d) => {
        if (d.quadrantId) {
          const quadrant = quadrantDict[d.quadrantId as string];
          d["sensorName"] = quadrant + "-" + d["sensorName"];
          // d['connectedTo'] = d.connectedTo
          //   .map((c) => {
          //     return c.quadrant + '-' + c.rackNo;
          //   })
          //   .join(', ');
          //   d['sensorName'] = d['sensorName'] + ' (' + quadrant + ')';
        }
        return d;
      });
      setReadings(result.data);
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        result.message || "Error while fetching sensors readings",
        "error"
      );
    }
  };

  const onRenderOption = (option?: ISelectableOption): JSX.Element => {
    if (!option) return <div />;
    return (
      <div className="d-flex">
        {/* {!option.itemType && <Icon path={mdiFloorPlan} size={0.8} />} */}
        <span style={{ marginLeft: option.itemType ? "0px" : "12px" }}>
          {option.text}
        </span>
      </div>
    );
  };

  const onRenderTitle = (options?: IDropdownOption[]): JSX.Element => {
    if (!options) return <div />;
    const option = options[0];

    return (
      <div>
        <span>{option.data.buildingName + "-" + option.text}</span>
      </div>
    );
  };
  const onSearch = () => {
    // validate
    if (isRackWiseView) {
      fetchRackWiseReadings();
    } else {
      searchMorReadings();
    }
  };

  const isTempNotInThreshold = (tempValue: number, customerId?: string) => {
    if (customerId) {
      if (!slaMatrixDictByCustomer[customerId].temperature) return false;
      if (
        tempValue < slaMatrixDictByCustomer[customerId].temperature.min ||
        tempValue > slaMatrixDictByCustomer[customerId].temperature.max
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (!slaMatrix.temperature) return false;
      if (
        tempValue < slaMatrix.temperature.min ||
        tempValue > slaMatrix.temperature.max
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isRhNotInThreshold = (rhValue: number, customerId?: string) => {
    if (customerId) {
      if (!slaMatrixDictByCustomer[customerId].humidity) return false;
      if (
        rhValue < slaMatrixDictByCustomer[customerId].humidity.min ||
        rhValue > slaMatrixDictByCustomer[customerId].humidity.max
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (!slaMatrix.humidity) return false;
      if (
        rhValue < slaMatrix.humidity.min ||
        rhValue > slaMatrix.humidity.max
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const handleDialogOpen = (
    isOpen: boolean,
    index?: number,
    rIndex?: number
  ) => {
    let data = undefined;
    if (rIndex != null) {
      data = rackWiseReadings[rIndex].tempRhReadings[index as number];
    } else if (index !== undefined) {
      data = { ...readings[index] };
    }
    setState({
      ...state,
      manageTempRhDialog: {
        isOpen,
        index,
        data,
      },
    });
  };

  const handleDialogClose = () => {
    setState({
      ...state,
      manageTempRhDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
    });
    // searchMorReadings();
  };

  // const handleLoading = () => {
  //   setLoading(!isLoading);
  // };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    index?: number,
    data?: IMorTempRhReadings
  ) => {
    setState({
      ...state,
      isOnEdit: false,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        index,
        data,
      },
    });
  };

  const _renderItemColumn = (item: any, index: number, column: IColumn) => {
    switch (column.key) {
      case "actions":
        return (
          <>
            {state.isOnEdit && state.manageActionsCallout.index === index ? (
              <Stack horizontal tokens={childrenGap}>
                <React.Fragment>
                  <TooltipHost
                    content={`Save`}
                    id={`saveTempRh`}
                    calloutProps={{ gapSpace: 0 }}
                    styles={{ root: { display: "inline-block" } }}
                  >
                    <span onClick={onUpdateTempRh}>
                      <Icon
                        path={mdiCheckCircleOutline}
                        size={0.8}
                        className="pointer"
                        color="green"
                      />
                    </span>
                  </TooltipHost>
                  <TooltipHost
                    content={`Cancel`}
                    id={`cancelTempRh`}
                    calloutProps={{ gapSpace: 0 }}
                    styles={{ root: { display: "inline-block" } }}
                  >
                    <span
                      onClick={() =>
                        setState({
                          ...state,
                          isOnEdit: false,
                          tempRhObj: {
                            date: new Date(),
                            temp: 0,
                            rh: 0,
                            sensorId: "",
                            quadrantId: "",
                          },
                          manageActionsCallout: {
                            isOpen: false,
                            index: undefined,
                            element: undefined,
                            data: undefined,
                          },
                        })
                      }
                    >
                      <Icon
                        path={mdiCloseCircleOutline}
                        size={0.8}
                        className="pointer"
                        color="red"
                      />
                    </span>
                  </TooltipHost>
                </React.Fragment>
              </Stack>
            ) : (
              <span
                onClick={(ele) =>
                  handleCallOutOpenClose(true, ele, index, item)
                }
              >
                <Icon
                  path={mdiDotsVertical}
                  size={0.9}
                  className="pointer"
                  color="#135c8a"
                />
              </span>
            )}
          </>
          // <Stack horizontal tokens={childrenGap}>
          //   <TooltipHost
          //     content={`View`}
          //     id={`viewTempRh`}
          //     calloutProps={{ gapSpace: 0 }}
          //     styles={{ root: { display: "inline-block" } }}
          //   >
          //     <span onClick={() => handleDialogOpen(true, index)}>
          //       <Icon
          //         path={mdiEyeOutline}
          //         size={0.8}
          //         className="pointer"
          //         color={THEME.palette.themePrimary}
          //       />
          //     </span>
          //   </TooltipHost>
          // </Stack>
        );
      case "temp":
        return (
          <React.Fragment>
            {state.isOnEdit && state.manageActionsCallout.index === index ? (
              <TextField
                value={String(state.tempRhObj.temp)}
                type="number"
                name="temp"
                onChange={handleChange}
              // className="width-80"
              />
            ) : (
              <React.Fragment>
                <span
                  className={
                    item.temp !== "NA" && isTempNotInThreshold(item.temp)
                      ? "text-danger"
                      : ""
                  }
                >
                  {toFixedValue(item.temp)}
                </span>
                {item.temp !== "NA" && isTempNotInThreshold(item.temp) && (
                  <Icon
                    path={
                      item.temp < slaMatrix.temperature.min
                        ? mdiArrowDown
                        : mdiArrowUp
                    }
                    size={0.5}
                    color="#D62828"
                    style={{ marginBottom: -2 }}
                  />
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        );
      case "rh":
        return (
          <React.Fragment>
            {state.isOnEdit && state.manageActionsCallout.index === index ? (
              <TextField
                value={String(state.tempRhObj.rh)}
                type="number"
                name="rh"
                onChange={handleChange}
              // className="width-80"
              />
            ) : (
              <React.Fragment>
                <span
                  className={
                    item.rh !== "NA" && isRhNotInThreshold(item.rh)
                      ? "text-danger"
                      : ""
                  }
                >
                  {toFixedValue(item.rh)}
                </span>
                {item.rh !== "NA" && isRhNotInThreshold(item.rh) && (
                  <Icon
                    path={
                      item.rh < slaMatrix.humidity.min
                        ? mdiArrowDown
                        : mdiArrowUp
                    }
                    size={0.5}
                    color="#D62828"
                    style={{ marginBottom: -2 }}
                  />
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        );
      case "connectedTo":
        return (
          <div>
            {/* <div className={item.connectedTo || 'bg-warning'}> */}
            {item.connectedTo}
          </div>
        );
      //   case 'date':
      //     return <span>{moment(item.date).format('hh:mm:ss')}</span>;
      case "sensorName":
        return (
          <Text styles={{ root: { fontWeight: 600 } }}>
            {item[column.fieldName as any]}
          </Text>
        );
      default:
        return <span>{column.fieldName ? item[column.fieldName] : ""}</span>;
    }
  };

  const handleChange = (e: any) => {
    setState({
      ...state,
      tempRhObj: {
        ...state.tempRhObj,
        [e.target.name]: e.target.value,
      },
    });
  };

  const onUpdateTempRh = async () => {
    let tempRhObj = { ...state.tempRhObj };
    tempRhObj.temp = Number(tempRhObj.temp);
    tempRhObj.rh = Number(tempRhObj.rh);

    if (!state.tempRhObj.temp) {
      tempRhObj.temp = 0;
    }
    if (!state.tempRhObj.rh) {
      tempRhObj.rh = 0;
    }
    setLoading(true);
    let result = await updateTempAndHumidity(tempRhObj);
    if (result?.success) {
      let allReadings = [...readings];
      if (state.manageActionsCallout.index !== undefined) {
        allReadings[state.manageActionsCallout.index].temp =
          state.tempRhObj.temp || 0;
        allReadings[state.manageActionsCallout.index].rh =
          state.tempRhObj.rh || 0;
      }
      setState({
        ...state,
        tempRhObj: {
          date: new Date(),
          temp: 0,
          rh: 0,
          sensorId: "",
          quadrantId: "",
        },
        isOnEdit: false,
        manageActionsCallout: {
          isOpen: false,
          index: undefined,
          element: undefined,
          data: undefined,
        },
      });
      showToast("Temp & Rh value updated successfully", "success");
    } else {
      showToast(result?.message || "Error while updating temp & rh", "error");
    }
    setLoading(false);
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const fetchRackWiseReadings = async (floorId?: string) => {
    setLoading(true);
    const searchObj = { ...search };
    if (floorId) {
      searchObj["floorId"] = floorId;
    }
    const result = await getMorTempAndRhReadingsRackWise(searchObj);
    if (result && result.success) {
      setRackWiseReadings(result.data.data);
      slaMatrixDictByCustomer = result.data.slaMatrix.reduce(
        (acc: any, d: ISLAMatrix) => {
          acc[d.customerId] = d;
          return acc;
        },
        {}
      );
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        result.message || "Error while fetching sensors readings",
        "error"
      );
    }
  };

  const handleChangeToggle = async (checked: boolean) => {
    if (checked === true) {
      await fetchRackWiseReadings();
    } else {
      setRackWiseReadings([]);
    }
    setIsRackWiseView(checked);
  };
  return (
    <React.Fragment>
      {isLoading && <Loading />}
      {/* <Stack>
        <Stack.Item>
          <Breadcrumb
            items={[
              {
                text: datacenter.name ? datacenter.name : "Datacenter",
                key: "datacenter",
              },
              {
                text: "Infrastructures (Automation)",
                key: "infrastructure-automation",
                onClick: () => {
                  history.goBack();
                },
              },
              {
                text: "Temperature and Humidity",
                key: "temp-rh",
                isCurrentItem: true,
              },
            ]}
          />
        </Stack.Item>
      </Stack> */}
      {/* <br /> */}
      <Stack horizontal horizontalAlign="start" className="pt-16">
        <Stack.Item>
          <Stack horizontal wrap tokens={childrenGap}>
            <Stack.Item>
              <Dropdown
                placeholder="Select a Floor"
                selectedKey={search.floorId}
                // defaultSelectedKey={state.searchObj.floorId}
                styles={{ dropdown: { width: 200 } }}
                options={floors}
                onRenderOption={onRenderOption}
                onRenderTitle={onRenderTitle}
                onChange={(e, v: IDropdownOption | undefined) => {
                  if (v) {
                    setSearch({ ...search, floorId: v.key as string });
                    // setState({
                    //   ...state,
                    //   searchObj: {
                    //     ...state.searchObj,
                    //     floorId: v.key as string,
                    //     // upsId: '',
                    //     upsId: state.upsObj[v.key][0].key,
                    //   },
                    //   allUpsDropdownList: state.upsObj[v.key],
                    // });
                  }
                }}
              />
            </Stack.Item>
            <Stack.Item>
              <DatePicker
                firstDayOfWeek={DayOfWeek.Sunday}
                showMonthPickerAsOverlay={true}
                value={new Date(search.startDate)}
                showGoToToday={false}
                onSelectDate={(date: Date | null | undefined) => {
                  //   setState({
                  //     ...state,
                  //     searchObj: { ...state.searchObj, date: date as Date },
                  //   })
                  setSearch({
                    ...search,
                    startDate: date as Date,
                    endDate: date as Date,
                  });
                }}
                placeholder="Select a date..."
                ariaLabel="Select a date"
                styles={{ root: { width: 200 } }}
              />
            </Stack.Item>
            <Stack.Item>
              <PrimaryButton text="Search" onClick={() => onSearch()} />
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
      <br />
      <Stack>
        <Stack.Item align="end">
          <Toggle
            className="custom-toggle"
            label="Show Rackwise Readings"
            checked={isRackWiseView}
            // onText='On'
            // offText='Off'
            onChange={(ev: any, checked?: boolean) => {
              handleChangeToggle(checked === true);
            }}
          />
        </Stack.Item>
      </Stack>
      <br />
      <Stack>
        <Stack.Item className="max-table-width">
          {readings && readings.length > 0 ? (
            <Fabric>
              {!isRackWiseView ? (
                <DetailsList
                  items={readings}
                  columns={[
                    // {
                    //   key: "date",
                    //   name: "Time",
                    //   fieldName: "date",
                    //   minWidth: 60,
                    //   maxWidth: 110,
                    //   isResizable: true,
                    // },
                    {
                      key: "sensorName",
                      name: "Sensor Name",
                      fieldName: "sensorName",
                      minWidth: 200,
                      maxWidth: 250,
                      isResizable: true,
                    },
                    {
                      key: "connectedTo",
                      name: "Connected Racks",
                      fieldName: "connectedTo",
                      minWidth: 550,
                      maxWidth: 650,
                      isResizable: true,
                    },
                    {
                      key: "type",
                      name: "Sensor Type",
                      fieldName: "type",
                      minWidth: 100,
                      maxWidth: 150,
                      isResizable: true,
                    },
                    {
                      key: "ip",
                      name: "IP",
                      fieldName: "ip",
                      minWidth: 100,
                      maxWidth: 150,
                      isResizable: true,
                    },
                    {
                      key: "tempObjectId",
                      name: "Temp Object Id",
                      fieldName: "tempObjectId",
                      minWidth: 100,
                      maxWidth: 150,
                      isResizable: true,
                    },
                    {
                      key: "rhObjectId",
                      name: "RH Object Id",
                      fieldName: "rhObjectId",
                      minWidth: 100,
                      maxWidth: 150,
                      isResizable: true,
                    },
                    {
                      key: "temp",
                      name: "Temperature",
                      fieldName: "temp",
                      minWidth: 150,
                      maxWidth: 200,
                      isResizable: true,
                    },
                    {
                      key: "rh",
                      name: "Humidity",
                      fieldName: "rh",
                      minWidth: 150,
                      maxWidth: 200,
                      isResizable: true,
                    },
                    {
                      key: "actions",
                      name: "Actions",
                      fieldName: "actions",
                      minWidth: 150,
                      maxWidth: 200,
                      isResizable: true,
                    },
                  ]}
                  compact={true}
                  setKey="set"
                  onRenderItemColumn={_renderItemColumn as any}
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                  onRenderRow={_onRenderRow}
                />
              ) : (
                <div className="office-table bg-white">
                  <table className="ms-Table bordered-table w-100">
                    <thead
                      className="html-table-thead"
                      style={{ fontSize: 14 }}
                    >
                      <tr style={{ height: 44 }}>
                        <th>Customer Name</th>
                        <th>Rack No</th>
                        <th>Sensors</th>
                        <th>Temperature</th>
                        <th>Humidity</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rackWiseReadings.reduce((acc: any, d, rIndex) => {
                        d.tempRhReadings.forEach((r: any, index) => {
                          acc.push(
                            <tr key={rIndex + "" + index}>
                              {index === 0 && (
                                <td rowSpan={d.tempRhReadings.length}>
                                  {d.customerName}
                                </td>
                              )}
                              {index === 0 && (
                                <td rowSpan={d.tempRhReadings.length}>
                                  {d.quadrantName + "-" + d.rackNo}
                                </td>
                              )}
                              <td>
                                {(d.quadrantName && d.quadrantName + "-") +
                                  r.sensorName}
                              </td>
                              <td>
                                <span
                                  className={
                                    r.temp !== "NA" &&
                                      isTempNotInThreshold(
                                        r.temp,
                                        slaMatrixDictByCustomer[d.customerId]
                                          ? d.customerId
                                          : undefined
                                      )
                                      ? "text-danger"
                                      : ""
                                  }
                                >
                                  {toFixedValue(r.temp)}
                                </span>
                                {r.temp !== "NA" &&
                                  isTempNotInThreshold(
                                    r.temp,
                                    slaMatrixDictByCustomer[d.customerId]
                                      ? d.customerId
                                      : undefined
                                  ) && (
                                    <Icon
                                      path={
                                        r.temp <
                                          (slaMatrixDictByCustomer[d.customerId]
                                            ? slaMatrixDictByCustomer[
                                              d.customerId
                                            ].temperature.min
                                            : slaMatrix.temperature.min)
                                          ? mdiArrowDown
                                          : mdiArrowUp
                                      }
                                      size={0.5}
                                      color="#D62828"
                                      style={{ marginBottom: -2 }}
                                    />
                                  )}
                              </td>
                              <td>
                                <span
                                  className={
                                    r.rh !== "NA" &&
                                      isRhNotInThreshold(
                                        r.rh,
                                        slaMatrixDictByCustomer[d.customerId]
                                          ? d.customerId
                                          : undefined
                                      )
                                      ? "text-danger"
                                      : ""
                                  }
                                >
                                  {toFixedValue(r.rh)}
                                </span>
                                {r.rh !== "NA" &&
                                  isRhNotInThreshold(
                                    r.rh,
                                    slaMatrixDictByCustomer[d.customerId]
                                      ? d.customerId
                                      : undefined
                                  ) && (
                                    <Icon
                                      path={
                                        r.rh <
                                          (slaMatrixDictByCustomer[d.customerId]
                                            ? slaMatrixDictByCustomer[
                                              d.customerId
                                            ].humidity.min
                                            : slaMatrix.humidity.min)
                                          ? mdiArrowDown
                                          : mdiArrowUp
                                      }
                                      size={0.5}
                                      color="#D62828"
                                      style={{ marginBottom: -2 }}
                                    />
                                  )}
                              </td>
                              <td align="center">
                                <TooltipHost
                                  content={`View`}
                                  id={`viewTempRh`}
                                  calloutProps={{ gapSpace: 0 }}
                                  styles={{
                                    root: { display: "inline-block" },
                                  }}
                                >
                                  <span
                                    onClick={() =>
                                      handleDialogOpen(true, index, rIndex)
                                    }
                                  >
                                    <Icon
                                      path={mdiEyeOutline}
                                      size={0.8}
                                      className="pointer"
                                      color={THEME.palette.themePrimary}
                                    />
                                  </span>
                                </TooltipHost>
                              </td>
                            </tr>
                          );
                        });
                        return acc;
                      }, [])}
                    </tbody>
                  </table>
                </div>
              )}
            </Fabric>
          ) : (
            <NoDataTile title="No Databases Found"></NoDataTile>
          )}
        </Stack.Item>
      </Stack>
      {state.manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 95, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            state.manageActionsCallout &&
            state.manageActionsCallout.data?.sensorId
          }
          gapSpace={0}
          target={state.manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
        // setInitialFocus={true}
        >
          <Stack tokens={childrenGap8}>
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  handleDialogOpen(true, state.manageActionsCallout.index);
                }}
                className="callout-options"
              >
                View
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  setState({
                    ...state,
                    tempRhObj: {
                      date: search.startDate,
                      temp: state.manageActionsCallout.data?.temp as number,
                      rh: state.manageActionsCallout.data?.rh as number,
                      sensorId: state.manageActionsCallout.data?.sensorId,
                    },
                    manageActionsCallout: {
                      ...state.manageActionsCallout,
                      isOpen: false,
                    },
                    isOnEdit: true,
                  });
                }}
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {state.manageTempRhDialog.isOpen && (
        <ManageTempAndRhDialog
          handleDialogClose={() => handleDialogClose()}
          data={state.manageTempRhDialog.data as IMorTempRhReadings}
          // handleLoading={handleLoading}
          floorId={search.floorId}
          date={search.startDate}
          // datacenterId={params.datacenterId}
          slaMatrix={slaMatrix}
        />
      )}
    </React.Fragment>
  );
};

export default TempAndRhMain;
